import { TableContext } from "../../context";
import React, { useState, useContext, useEffect, useMemo } from "react";
import "../../App.css";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { DateTime } from 'luxon';
import {
    McTable,
    McButton,
    McPagination
} from "@maersk-global/mds-react-wrapper";
import FilterButton from "../FilterButton";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

/**
 * Table component for displaying and filtering table data.
 * Provides options to filter data by risk, state, type, and category.
 * Allows downloading the filtered data as a CSV file.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.Header1 - The first header for the table.
 * @param {string} props.Header2 - The second header for the table.
 * @param {string} props.Header3 - The third header for the table.
 * @param {string} props.Header4 - The fourth header for the table.
 * @param {string} props.Header5 - The fifth header for the table.
 * @param {string} props.Header6 - The sixth header for the table.
 * @param {string} props.Header7 - The seventh header for the table.
 * @param {string} props.Header8 - The eighth header for the table.
 * @param {string} props.Header9 - The ninth header for the table.
 * @param {string} props.Header10 - The tenth header for the table.
 * @param {string} props.Header11 - The eleventh header for the table.
 */
export default function StatusPageTable(props) {
    const tableData = props.tableData;
    const [activeFilters, setActiveFilters] = useState([]);
    const [tableStyles, setTableStyles] = useState();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 100; // Number of records per page

    // Table state preservation
    const [sortColumn, setSortColumn] = useState("plannedStartDate");
    const [sortDirection, setSortDirection] = useState("descending");
    const [tableScrollPosition, setTableScrollPosition] = useState(0);

    // Define table columns for the filter system and McTable
    const tableColumns = [
        {
            id: "changeNumber",
            label: props.Header1 || "Change Number",
            sortable: true,
        },
        {
            id: "category",
            label: props.Header2 || "Category",
            sortable: true,
        },
        {
            id: "applicationName",
            label: props.Header3 || "Application Name",
            sortable: true,
        },
        {
            id: "plannedStartDate",
            label: props.Header4 || "Planned Start Date",
            sortable: true,
        },
        {
            id: "plannedEndDate",
            label: props.Header5 || "Planned End Date",
            sortable: true,
        },
        {
            id: "shortDescription",
            label: props.Header6 || "Short Description",
            sortable: true,
            nowrap: false,
        },
        {
            id: "riskImpact",
            label: props.Header7 || "Risk Impact",
            sortable: true,
        },
        {
            id: "prechangeTestStatus",
            label: props.Header8 || "Prechange Test Status",
            sortable: true,
        },
        {
            id: "prechangeFailureEndpoints",
            label: props.Header9 || "Prechange Failure Endpoints",
            sortable: true,
        },
        {
            id: "postchangeTestStatus",
            label: props.Header10 || "Postchange Test Status",
            sortable: true,
        },
        {
            id: "postchangeFailureEndpoints",
            label: props.Header11 || "Postchange Failure Endpoints",
            sortable: true,
        }
    ];

    // Filter columns for the advanced filter (using original field names)
    const filterColumns = [
        { id: "Status Page Prechange Test", label: "Prechange Test Status" },
        { id: "Status Page Postchange Test", label: "Postchange Test Status" },
        { id: "Change Number", label: "Change Number" },
        { id: "Application Name", label: "Application Name" }, 
        { id: "Category", label: "Category" },
        { id: "Risk Impact", label: "Risk" },     
        { id: "Planned Start Date", label: "Planned Start Date" },
        { id: "Planned End Date", label: "Planned End Date" }
    ];

    const sortArrayByProperty = (data, property, order = 'ascending') => {
        return data.sort((a, b) => {
            let comparison = 0;

            if (property === "plannedStartDate" || property === "plannedEndDate") {
                const dateA = DateTime.fromFormat(a[property], 'dd-MM-yyyy HH:mm:ss');
                const dateB = DateTime.fromFormat(b[property], 'dd-MM-yyyy HH:mm:ss');
                if (dateA < dateB) comparison = -1;
                else if (dateA > dateB) comparison = 1;
            }
            else if (property === "changeNumber") {
                const numberA = parseInt(a[property].replace(/[^0-9]/g, ''));
                const numberB = parseInt(b[property].replace(/[^0-9]/g, ''));
                if (numberA < numberB) comparison = -1;
                else if (numberA > numberB) comparison = 1;
            }
            else {
                if (a[property] < b[property]) comparison = -1;
                else if (a[property] > b[property]) comparison = 1;
            }
            return order === 'ascending' ? comparison : comparison * -1;
        });
    }

    // Apply advanced filters from the filter menu
    const applyAdvancedFilters = (data, filters) => {
        if (!filters || filters.length === 0) return data;

        return data.filter((item) => {
            return filters.every((filter) => {
                let itemValue = item[filter.field];
                const filterValue = filter.value.toLowerCase();

                if (itemValue === undefined || itemValue === null) return false;

                if (
                    filter.field === "Planned Start Date" ||
                    filter.field === "Planned End Date"
                ) {
                    // Handle date filtering if needed
                    itemValue = DateTime.fromFormat(itemValue, 'dd-MM-yyyy HH:mm:ss').toFormat('dd-MM-yyyy HH:mm:ss');
                }

                const itemString = String(itemValue).toLowerCase();

                switch (filter.operator) {
                    case "contains":
                        return itemString.includes(filterValue);
                    case "Does not contain":
                        return !itemString.includes(filterValue);
                    case "equals":
                        return itemString === filterValue;
                    case "Is not":
                        return itemString !== filterValue;
                    case "startsWith":
                        return itemString.startsWith(filterValue);
                    case "endsWith":
                        return itemString.endsWith(filterValue);
                    default:
                        return itemString.includes(filterValue);
                }
            });
        });
    };

    const filteredData = useMemo(() => {
        if (!tableData) return [];

        // Apply advanced filters
        let filtered = applyAdvancedFilters(tableData, activeFilters);

        // Ensure filtered is always an array
        if (!Array.isArray(filtered)) filtered = [];

        // Default sort by Planned Start Date in descending order
        filtered.sort((a, b) => {
            const dateA = DateTime.fromFormat(a["Planned Start Date"], 'dd-MM-yyyy HH:mm:ss');
            const dateB = DateTime.fromFormat(b["Planned Start Date"], 'dd-MM-yyyy HH:mm:ss');
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
        });

        return filtered;
    }, [tableData, activeFilters]);

    // Client-side pagination - slice data before transformation
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    const transformedData = useMemo(() => {
        let dataToSort = paginatedData.map((item, index) => {
            return {
                id: `row-${index}`,
                changeNumber: item["Change Number"],
                category: item["Category"],
                applicationName: item["Application Name"],
                plannedStartDate: item["Planned Start Date"],
                plannedEndDate: item["Planned End Date"],
                shortDescription: item["Short Description"],
                riskImpact: item["Risk Impact"],
                prechangeTestStatus: item["Status Page Prechange Test"],
                prechangeFailureEndpoints: item["Status Page Prechange Failure URLs"],
                postchangeTestStatus: item["Status Page Postchange Test"],
                postchangeFailureEndpoints: item["Status Page Postchange Failure URLs"],
                _originalData: item
            };
        });

        if (sortColumn) {
            dataToSort = sortArrayByProperty([...dataToSort], sortColumn, sortDirection);
        }

        return dataToSort;
    }, [paginatedData, sortColumn, sortDirection]);

    const handlePageChange = (event) => {
        setCurrentPage(event.detail);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    useEffect(() => {
        if (tableScrollPosition > 0) {
            const tableElement = document.querySelector('mc-table');
            if (tableElement) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }
    }, [transformedData]);

    useEffect(() => {
        setTableStyles(`
            .mds-table {
                max-height: 65vh !important;
            }
        `);
    }, [transformedData]);

    const getTestStatusColor = (testStatus) => {
        switch (testStatus) {
            case 'HEALTHY':
                return '#13a10e';
            case 'UNHEALTHY':
                return '#d13438';
            case 'N/A':
                return '#888';
            default:
                return 'black';
        }
    };

    const handleTableSorted = ({ detail: { column, direction } }) => {
        setSortColumn(column);
        setSortDirection(direction);

        // Preserve scroll position after sorting
        setTimeout(() => {
            const tableElement = document.querySelector('mc-table');
            if (tableElement && tableScrollPosition > 0) {
                const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                    tableElement.querySelector('.table-container');
                if (scrollContainer) {
                    scrollContainer.scrollTop = tableScrollPosition;
                }
            }
        }, 100);
    }

    // Save scroll position before interactions
    const saveScrollPosition = () => {
        const tableElement = document.querySelector('mc-table');
        if (tableElement) {
            const scrollContainer = tableElement.shadowRoot?.querySelector('.table-container') ||
                tableElement.querySelector('.table-container');
            if (scrollContainer) {
                setTableScrollPosition(scrollContainer.scrollTop);
            }
        }
    };

    const handleDownloadClick = () => {
        const headers = [
            props.Header1,
            props.Header2,
            props.Header3,
            props.Header4,
            props.Header5,
            props.Header6,
            props.Header7,
            props.Header8,
            props.Header9,
            props.Header10,
            props.Header11,
        ];

        const flattenedData = filteredData.map((item) => ({
            "Change Number": item["Change Number"],
            "Category": item["Category"],
            "Application Name": item["Application Name"],
            "Planned Start Date": item["Planned Start Date"],
            "Planned End Date": item["Planned End Date"],
            "Short Description": item["Short Description"],
            "Risk Impact": item["Risk Impact"],
            "Prechange Test Status": item["Status Page Prechange Test"],
            "Prechange Failure Endpoints": item["Status Page Prechange Failure URLs"].join(", "),
            "Postchange Test Status": item["Status Page Postchange Test"],
            "Postchange Failure Endpoints": item["Status Page Postchange Failure URLs"].join(", "),
        }));

        const csv = Papa.unparse({
            fields: headers,
            data: flattenedData,
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "Status Page Results.csv");
    };

    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
    };

    const handleChangeNumberClick = (rowData, drawerType) => {
        saveScrollPosition();
        const changeNumber = rowData["Change Number"];
        if (changeNumber) {
            // Open change details in a new tab
            const url = `/change_details?changeNumber=${encodeURIComponent(changeNumber)}`;
            window.open(url, '_blank');
        }
    };

    const handleCiInfoClick = (rowData) => {
        saveScrollPosition();

        setTimeout(() => {
            props.setisdrawerOpen(true);
            props.setDrawerCi(rowData["Application Name"]);
            props.setDrawerCiCategory(rowData["Category"]);
            props.setDrawerType("ci_info");
        }, 0);
    };

    return (
        <div className="tests-table-container" style={{ marginTop: "10px" }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    position: 'relative',
                }}
            >
                <div style={{ flex: '0 0 auto' }}>
                    <FilterButton
                        columns={filterColumns}
                        data={tableData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        width: 'max-content',
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ fontWeight: "600", fontSize: '1.2rem', color: "#555" }}>
                        Status Page Test Results
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: '0 0 auto' }}>
                    {!props.isLoading && 
                        <span style={{ fontSize: '1rem', color: '#0077b2' }}>
                            Total: {filteredData.length}
                        </span>
                    }
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                    >
                        <span slot="trailingicon">
                            <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                    </McButton>
                </div>
            </div>
            <div>
                <McTable
                    data={transformedData}
                    columns={tableColumns}
                    datakey="id"
                    headersticky
                    fit="small"
                    sortmanual
                    sortchange={handleTableSorted}
                    sortdefaultcolumnid={sortColumn}
                    sortdefaultdirection={sortDirection}
                    customstyles={tableStyles}
                    resetscrollonpagechange={false}
                    stateslotheight="200px"
                >
                    {props.isLoading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                    ) : (
                        transformedData?.length === 0 && (
                            <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                                No Data Found
                            </div>
                        )
                    )}
                    {transformedData?.map((row) => (
                        <React.Fragment key={row.id}>
                            {/* Change Number Cell */}
                            <div slot={`${row.id}_changeNumber`}>
                                <span
                                    onClick={() => handleChangeNumberClick(row._originalData, "change_details")}
                                    style={{
                                        color: "#0077b2",
                                        textDecoration: "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    {row.changeNumber}
                                </span>
                            </div>

                            {/* Application Name Cell */}
                            <div
                                slot={`${row.id}_applicationName`}
                                style={{
                                    cursor: row._originalData["Category"] === "Application" ? "pointer" : undefined
                                }}
                                onClick={row._originalData["Category"] === "Application" ? () => handleCiInfoClick(row._originalData) : undefined}
                            >
                                <div>
                                    {row._originalData["Category"] === "Application" ? (
                                        <div style={{ alignItems: 'center' }}>
                                            {row.applicationName}
                                            <Tooltip title="Show CI Info">
                                                <IconButton
                                                    style={{
                                                        marginLeft: '0.2rem',
                                                        padding: 0,
                                                        verticalAlign: 'top'
                                                    }}
                                                    tabIndex={-1}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleCiInfoClick(row._originalData);
                                                    }}
                                                    aria-label="Show CI info"
                                                >
                                                    <InfoOutlinedIcon fontSize="small" style={{ fontWeight: "normal" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    ) : (
                                        row.applicationName
                                    )}
                                </div>
                            </div>

                            {/* Short Description Cell */}
                            <div slot={`${row.id}_shortDescription`}>
                                <span style={{ maxWidth: "400px", wordBreak: "break-word" }}>
                                    {row.shortDescription}
                                </span>
                            </div>

                            {/* Risk Impact Cell */}
                            <div slot={`${row.id}_riskImpact`}>
                                {row.riskImpact}
                            </div>

                            {/* Prechange Test Status Cell */}
                            <div slot={`${row.id}_prechangeTestStatus`}>
                                <span style={{ color: getTestStatusColor(row.prechangeTestStatus) }}>
                                    {row.prechangeTestStatus}
                                </span>
                            </div>

                            {/* Prechange Failure Endpoints Cell */}
                            <div slot={`${row.id}_prechangeFailureEndpoints`}>
                                {(row.prechangeFailureEndpoints ?? []).map((url, index) => {
                                    if (url === "N/A") {
                                        return (
                                            <div key={index} style={{ color: "#888" }}>{url}</div>
                                        );
                                    } else if (!url.includes(":-:")) {
                                        return (
                                            <div key={index} style={{ color: "#d13438" }}>{url}</div>
                                        );
                                    } else {
                                        const [linkText, linkUrl] = url.split(":-:");
                                        return (
                                            <a
                                                key={index}
                                                href={linkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#0077b2", textDecoration: "none" }}
                                            >
                                                {linkText}
                                            </a>
                                        );
                                    }
                                })}
                            </div>

                            {/* Postchange Test Status Cell */}
                            <div slot={`${row.id}_postchangeTestStatus`}>
                                <span style={{ color: getTestStatusColor(row.postchangeTestStatus) }}>
                                    {row.postchangeTestStatus}
                                </span>
                            </div>

                            {/* Postchange Failure Endpoints Cell */}
                            <div slot={`${row.id}_postchangeFailureEndpoints`}>
                                {(row.postchangeFailureEndpoints ?? []).map((url, index) => {
                                    if (url === "N/A") {
                                        return (
                                            <div key={index} style={{ color: "#888" }}>{url}</div>
                                        );
                                    } else if (!url.includes(":-:")) {
                                        return (
                                            <div key={index} style={{ color: "#d13438" }}>{url}</div>
                                        );
                                    } else {
                                        const [linkText, linkUrl] = url.split(":-:");
                                        return (
                                            <a
                                                key={index}
                                                href={linkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#0077b2", textDecoration: "none" }}
                                            >
                                                {linkText}
                                            </a>
                                        );
                                    }
                                })}
                            </div>
                        </React.Fragment>
                    ))}
                </McTable>

                {/* Pagination Component */}
                {!props.isLoading && filteredData.length > recordsPerPage && (
                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <McPagination
                            currentpage={currentPage}
                            totalpages={totalPages}
                            pagechange={handlePageChange}
                        />
                    </div>
                )}
            </div>
            <div style={{ marginTop: "8vh", visibility: "hidden" }}>
                ""
            </div>
        </div>
    );
}